import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { Link } from '../Link';
import { Tooltip } from '../Tooltip';
import ToolSwitcher from '../ToolSwitcher';
import { isToolsPage, getCurrentToolRoute } from '../../utils/routing/route-utils';
import { Logo } from './Logo';
import { LanguageSelector } from './LanguageSelector';
import { UserMenu } from './UserMenu';
import { ToolsMenu } from './ToolsMenu';
import type { IntlShape } from 'react-intl';
import type { Tool } from './ToolsMenu';
import { useJobIslandContext } from '../../contexts/JobIslandContext';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';

export interface NavigationNavbarProps {
  intl: IntlShape;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: {
    image?: string;
    picture?: string;
    name?: string;
  } | null;
}

/**
 * Navigation navbar component - shows on non-SRP pages
 * Contains logo, navigation links, tools menu, language selector, and user menu
 */
export const NavigationNavbar: React.FC<NavigationNavbarProps> = ({
  intl,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  clearSearch,
  user,
}) => {
  const pageContext = usePageContext();
  const jobIsland = useJobIslandContext();
  const pathname = jobIsland?.pathname ?? (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  // Prefer job island, then pageContext, then intl, then pathname so static and hydrated both show correct locale
  const rawLocale = jobIsland?.locale ?? pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en';
  const locale = normalizeSupportedLocale(rawLocale);
  const searchPageUrl = `/${locale}`;
  const resourcesPath = locale === 'fi' ? 'tyoelama' : locale === 'sv' ? 'arbetsliv' : 'resources';
  const locationsPath = locale === 'fi' ? 'sijainnit' : locale === 'sv' ? 'platser' : 'locations';

  // Detect if we're on a tools page (prefer job island context when mounted in job island)
  const onToolsPage = isToolsPage(pathname);
  const currentToolRoute = getCurrentToolRoute(pathname) || '/';

  // Get tools list (same as ToolSwitcher)
  const tools: Tool[] = [
    { route: '/tools/job-flow-sankey', label: intl.formatMessage({ id: 'tools.jobFlow.link', defaultMessage: 'Job Flow', description: 'Job flow visualization tool link in navigation' }) },
    { route: '/tools/viewed-jobs', label: intl.formatMessage({ id: 'tools.viewedJobs.link', defaultMessage: 'Viewed Jobs', description: 'Viewed jobs page link in navigation' }) },
    { route: '/tools/saved-jobs', label: intl.formatMessage({ id: 'tools.savedJobs.link', defaultMessage: 'Saved Jobs', description: 'Saved jobs page link in navigation' }) },
    { route: '/tools/applied-jobs', label: intl.formatMessage({ id: 'tools.appliedJobs.link', defaultMessage: 'Applied Jobs', description: 'Applied jobs page link in navigation' }) },
    { route: '/tools/interviewing-jobs', label: intl.formatMessage({ id: 'tools.interviewingJobs.link', defaultMessage: 'Interviewing Jobs', description: 'Interviewing jobs page link in navigation' }) },
    { route: '/tools/offer-jobs', label: intl.formatMessage({ id: 'tools.offerJobs.link', defaultMessage: 'Offer Jobs', description: 'Offer jobs page link in navigation' }) },
    { route: '/tools/rejected-jobs', label: intl.formatMessage({ id: 'tools.rejectedJobs.link', defaultMessage: 'Rejected Jobs', description: 'Rejected jobs page link in navigation' }) },
    { route: '/tools/hired-jobs', label: intl.formatMessage({ id: 'tools.hiredJobs.link', defaultMessage: 'Hired Jobs', description: 'Hired jobs page link in navigation' }) },
    { route: '/tools/hidden-jobs', label: intl.formatMessage({ id: 'tools.hiddenJobs.link', defaultMessage: 'Hidden Jobs', description: 'Hidden jobs page link in navigation' }) },
    { route: '/tools/saved-searches', label: intl.formatMessage({ id: 'tools.recentSearches.link', defaultMessage: 'Saved Searches', description: 'Saved searches page link in navigation' }) },
  ];

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40" role="banner">
      <nav className="flex items-center gap-3 py-3 px-4 xl:px-20 md:px-10" aria-label="Main navigation">
        {/* Left side: Logo */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Logo intl={intl} clearSearch={clearSearch} variant="horizontal" />
          <Logo intl={intl} clearSearch={clearSearch} variant="compact" />
        </div>

        {/* Center: ToolSwitcher on tools pages (desktop), Tools button (mobile) */}
        {onToolsPage ? (
          <>
            {/* Desktop: ToolSwitcher */}
            <div className="hidden 2xl:flex flex-1 justify-center">
              <ToolSwitcher currentTool={currentToolRoute} variant="navbar" />
            </div>
            {/* Mobile: Tools button with dropdown */}
            <ToolsMenu
              intl={intl}
              currentToolRoute={currentToolRoute}
              tools={tools}
            />
          </>
        ) : (
          <nav className="hidden lg:flex items-center gap-4 flex-1 justify-center">
            <Tooltip
              content={intl.formatMessage({ id: 'tooltip.nav.home', defaultMessage: 'Return to the homepage', description: 'Tooltip text for home navigation link' })}
              position="bottom"
            >
              <Link
                href="/"
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FormattedMessage id="navigation.home" defaultMessage="Home" description="Navigation link to home page" />
              </Link>
            </Tooltip>
            <Tooltip
              content={intl.formatMessage({ id: 'tooltip.nav.resources', defaultMessage: 'Browse articles and guides about working in Finland', description: 'Tooltip text for resources/blog navigation link' })}
              position="bottom"
            >
              <Link
                href={`/${locale}/${resourcesPath}`}
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FormattedMessage id="usermenu.blog.link" defaultMessage="Resources" description="Navigation link to resources/blog page" />
              </Link>
            </Tooltip>
            <Tooltip
              content={intl.formatMessage({ id: 'tooltip.nav.locations', defaultMessage: 'Explore job markets by city and region', description: 'Tooltip text for locations hub navigation link' })}
              position="bottom"
            >
              <Link
                href={`/${locale}/${locationsPath}`}
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FormattedMessage id="navigation.locations" defaultMessage="Locations" description="Navigation link to locations hub page" />
              </Link>
            </Tooltip>
            <Tooltip
              content={intl.formatMessage({ id: 'tooltip.nav.search', defaultMessage: 'Search and filter job listings', description: 'Tooltip text for search navigation link' })}
              position="bottom"
            >
              <Link
                href={searchPageUrl}
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FormattedMessage id="navigation.search" defaultMessage="Search Jobs" description="Navigation link to job search page" />
              </Link>
            </Tooltip>
          </nav>
        )}

        {/* Right side: Language Selector (xl screens and above) and Hamburger Menu */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <LanguageSelector intl={intl} locale={locale} />
          <UserMenu
            intl={intl}
            user={user}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
          />
        </div>
      </nav>
    </header>
  );
};
